import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Wallet.scss";
import {
  Col,
  Row,
  Typography,
  Flex,
  Tooltip,
  Checkbox,
  List,
  Card,
  Tabs,
  Dropdown,
  Image,
  App,
} from "antd";
import { InfoCircleFilled, ArrowDownOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { filterOptions } from "../../utils/data";
import {
  DateFormatter,
  formatCurrencyTwoDec,
  toTitleCase,
  downloadBlob,
  getDownloadSuccessMessage,
} from "../../helpers/helpers";
import Deposit from "./Components/Deposit";
import Withdraw from "./Components/Withdraw";
import InvestingSteps from "./Components/InvestingSteps";
import PdfIcon from "../../assets/icons/pdf-icon.svg";
import CsvIcon from "../../assets/icons/csv-icon.svg";
import {
  getAccountBalanceInfo,
  getAccountTxnHistory,
  getBankDetailsByOrgId,
  getEscrowDetailsByOrgId,
} from "../../redux/slices/manageFundSlice";
import Loader from "../../components/CustomLoader/Loader";
import axios from "../../config/httpConfig";

const { Title, Text } = Typography;
const CheckboxGroup = Checkbox.Group;

const filterParamMapping = {
  1: "withdraw",
  2: "deposit",
  3: "dealPurchase",
  4: "dealRepayment",
};

const Wallet = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    availableBalance,
    bankDetailsData,
    txnHistoryData,
    escrowBankDetailsData,
    loading,
    txnHisLoading
  } = useSelector((state) => state.manageFunds);
  const [checkedFilters, setCheckedFilters] = useState([]);
  const { message } = App.useApp();

  useEffect(() => {
    dispatch(getAccountBalanceInfo(user?.orgId));
    dispatch(getBankDetailsByOrgId(user?.orgId));
    dispatch(getEscrowDetailsByOrgId(user?.orgId));
  }, [dispatch, user?.orgId]);

  const getApiParams = useCallback((filters) => {
    const params = {};
    Object.entries(filterParamMapping).forEach(([optionId, paramName]) => {
      params[paramName] = filters.includes(optionId);
    });
    return params;
  }, []);

  useEffect(() => {
    dispatch(getAccountTxnHistory(getApiParams(checkedFilters)));
  }, [dispatch, checkedFilters, getApiParams]);

  const onFilterChange = (list) => {
    setCheckedFilters(list);
  };

  const downloadTxnStatement = useCallback(async (format) => {
    try {
      const response = await axios.get("/api/investor/downloadTxnStatement", {
        params: {
          format,
          ...getApiParams(checkedFilters)
        },
        responseType: "blob",
      });
      const fileName = "Transaction Summary";
      const downloadedFileName = `${fileName}.${format === "pdf" ? "pdf" : "xlsx"}`;
      downloadBlob(response, downloadedFileName);
      message.success(getDownloadSuccessMessage(downloadedFileName));
    } catch (error) {
      message.error("Failed to download the report. Please try again later.");
    }
  }, [checkedFilters, getApiParams, message]);

  const items = useMemo(() => [
    {
      key: "1",
      icon: <Image width={20} src={PdfIcon} alt="PDF-Icon" preview={false} />,
      label: (
        <span
          onClick={() => downloadTxnStatement("pdf")}
          style={{ margin: 10 }}
        >
          Download as PDF
        </span>
      ),
    },
    {
      type: "divider",
    },
    {
      key: "2",
      icon: <Image width={20} src={CsvIcon} alt="CSV-Icon" preview={false} />,
      label: (
        <span
          onClick={() => downloadTxnStatement("xlsx")}
          style={{ margin: 10 }}
        >
          Download as CSV
        </span>
      ),
    },
  ], [downloadTxnStatement]);

  const txnDesc = (date, txnId, isVerified) => {
    return (
      <Flex>
        <Text style={{ color: "#40566D", fontSize: 12 }}>
          {DateFormatter(date)} • Transaction ID: {txnId} •{" "}
          {isVerified === 'Failed' ? (
            <Text style={{ color: '#FF0000', fontSize: 12 }}><CloseCircleOutlined /> Failed</Text>
          ) : (
            <Text style={{ fontSize: 12 }}>{isVerified || 'Unknown'}</Text>
          )}
        </Text>
      </Flex>
    );
  };

  const tabItems = useMemo(() => [
    {
      key: "1",
      label: "DEPOSIT",
      children: <Deposit escrowBankDetailsData={escrowBankDetailsData} />,
    },
    {
      key: "2",
      label: "WITHDRAW",
      children: (
        <Withdraw
          bankDetailsData={bankDetailsData}
          withdrawableAmt={availableBalance?.availableBalance}
        />
      ),
    },
  ], [escrowBankDetailsData, bankDetailsData, availableBalance?.availableBalance]);

  const getTxnTitle = (item) => {
    return (
      <Text style={{ fontWeight: 500 }}>
        {item?.purpose === "withdraw"
          ? "Withdrawal"
          : item?.purpose === "deposit"
            ? "Deposit"
            : `${item?.purpose} for ${toTitleCase(item?.DealView?.entName)}`}
      </Text>
    );
  };

  // Stamp fee is an internal accounting transfer to Karncy; hide it from the
  // investor-facing wallet history while keeping the ledger entry intact.
  const visibleTxnHistory = useMemo(
    () =>
      (txnHistoryData || []).filter(
        ({ purpose }) =>
          !String(purpose || "")
            .toLowerCase()
            .startsWith("stamp fee")
      ),
    [txnHistoryData]
  );

  return (
    <>
      <Title level={3} style={{ margin: 0, fontSize: 24 }}>
        Manage Funds
      </Title>
      {loading ? (
        <Loader />
      ) : (
        <Row
          gutter={[32, 32]}
          style={{ height: "100%", paddingTop: 20, paddingBottom: 20 }}
        >
          <Col xs={24} lg={16}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} lg={6}>
                <div className="manage-funds-cards">
                  <Flex gap="small">
                    <Text>Available Balance</Text>
                    <Tooltip
                      placement="right"
                      title="Balance available in investor wallet"
                    >
                      <InfoCircleFilled
                        style={{ color: "#768EA7", fontSize: 12 }}
                        rotate={180}
                      />
                    </Tooltip>
                  </Flex>
                  <Title level={5}>
                    {formatCurrencyTwoDec(availableBalance?.availableBalance || "0")}
                  </Title>
                </div>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <div className="manage-funds-cards">
                  <Flex gap="small">
                    <Text>Pending Payouts</Text>
                    <Tooltip
                      placement="right"
                      title="Amounts the investors supposed to receive back of the deals that are yet to be matured"
                    >
                      <InfoCircleFilled
                        style={{ color: "#768EA7", fontSize: 12 }}
                        rotate={180}
                      />
                    </Tooltip>
                  </Flex>
                  <Title level={5}>
                    {formatCurrencyTwoDec(availableBalance?.pendingPayouts || "0")}
                  </Title>
                </div>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <div className="manage-funds-cards">
                  <Flex gap="small">
                    <Text>Balance on Hold</Text>
                    <Tooltip
                      placement="right"
                      title="Related to TDS, sanction and withhold amount not approved from castler"
                    >
                      <InfoCircleFilled
                        style={{ color: "#768EA7", fontSize: 12 }}
                        rotate={180}
                      />
                    </Tooltip>
                  </Flex>
                  <Title level={5}>
                    {formatCurrencyTwoDec(availableBalance?.availableOnHold || "0")}
                  </Title>
                </div>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <div className="manage-funds-cards">
                  <Flex gap="small" wrap>
                    <Text>Parked for Investment</Text>
                    <Tooltip
                      placement="right"
                      title="Amount on hold where the deals status is in Funded or agreement stage"
                    >
                      <InfoCircleFilled
                        style={{ color: "#768EA7", fontSize: 12 }}
                        rotate={180}
                      />
                    </Tooltip>
                  </Flex>
                  <Title level={5}>
                    {formatCurrencyTwoDec(availableBalance?.parkForInv || "0")}
                  </Title>
                </div>
              </Col>
            </Row>
            <div className="bank-deatils-card-mobile">
              <Tabs defaultActiveKey="1" items={tabItems} />
            </div>
            <div className="transaction-container">
              <Flex align="center" justify="space-between">
                <Title level={4}>Transactions</Title>
                <Dropdown
                  menu={{ items }}
                  trigger={["click"]}
                  arrow
                  placement="bottom"
                >
                  <Flex
                    gap="small"
                    align="center"
                    style={{ cursor: "pointer" }}
                  >
                    <ArrowDownOutlined style={{ color: "#40566D" }} />
                    <Text style={{ color: "#40566D", fontWeight: 500 }}>
                      Download History
                    </Text>
                  </Flex>
                </Dropdown>
              </Flex>
              <div className="transaction-filter-container">
                <Text style={{ color: "#40566D" }}>Filter By</Text>
                <CheckboxGroup
                  options={filterOptions.map((option) => ({
                    label: option.optionLabel,
                    value: option.optionId,
                  }))}
                  value={checkedFilters}
                  onChange={onFilterChange}
                />
              </div>
              <Card className="transactions-card">
                <List
                  itemLayout="horizontal"
                  dataSource={visibleTxnHistory}
                  loading={txnHisLoading}
                  pagination={{ position: "bottom", align: "center" }}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <ArrowDownOutlined
                            style={{
                              color:
                                item.transactionType === "debit"
                                  ? "#101924"
                                  : "#00874F",
                            }}
                            rotate={
                              item.transactionType === "debit" ? 135 : 315
                            }
                          />
                        }
                        title={getTxnTitle(item)}
                        description={txnDesc(
                          item?.transactionDate,
                          item?.appTransactionId,
                          item?.transactionStatusId
                        )}
                      />
                      <Text
                        style={{
                          color:
                            item.transactionType === "debit"
                              ? "#101924"
                              : "#00874F",
                        }}
                        strong
                      >
                        {formatCurrencyTwoDec(item?.amount)}
                      </Text>
                    </List.Item>
                  )}
                />
              </Card>
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="bank-deatils-card-web">
              <Tabs defaultActiveKey="1" items={tabItems} />
            </div>
            <InvestingSteps />
          </Col>
        </Row>
      )}
    </>
  );
};

export default Wallet;
